import axios from 'axios';
import { t } from 'i18next';

import { authenticationAPIService } from '~/api/login';
import { MAX_ROTATE_VALUE } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import { cookieService } from '~/services/cookie-service';
import { googleAnalytics } from '~/services/google-analytics-service';
import { stepperSlice } from '~/store/reducers/stepepr';

// Client Width and Mobile Resolution Detection
export const isMobileResolution = navigator.userAgent.indexOf('Mobi') > -1;

// Type for Params in `getInitialRoute`
interface GetInitialRouteParams {
  isAuthenticated: boolean;
  pathname: string;
}

// `getInitialRoute` function with typed parameters
export const getInitialRoute = ({ isAuthenticated, pathname }: GetInitialRouteParams): string | undefined => {
  const isMiscrositePage = pathname?.slice(0, 3) === '/s/' && pathname.includes('/s/');
  const isLockedScreen = cookieService.getCookie('isFacebookLoginNotValid');

  const backRedirectRoutes = [
    PATH.LOGIN,
    PATH.SIGN_UP,
    PATH.CONFIRMATION,
    PATH.UNAUTH.MAIN,
    PATH.UNAUTH.QRS_ON,
    PATH.UNAUTH.QRS_ON_DETAIL('menu'),
    PATH.UNAUTH.PRICES_AND_PLANS,
    PATH.UNAUTH.FAQ,
    PATH.UNAUTH.PRIVACY_POLICY,
    '/contact-us',
  ];
  const isAlwaysAvailable = [
    PATH.QR.LIMIT_REACHED,
    PATH.QR.NOT_FOUND,
    PATH.TOS,
    PATH.UNAUTH.PRIVACY_POLICY,
    PATH.FACEBOOK_LOGIN,
    PATH.QR.DEACTIVATED,
    PATH.UNAUTH.DATA_DELETION,
  ];

  if (isLockedScreen && !isAlwaysAvailable.includes(pathname)) {
    return PATH.FACEBOOK_LOGIN;
  }

  if (isAlwaysAvailable.includes(pathname)) return;

  for (let i = 0; i < backRedirectRoutes.length; i++) {
    const item = backRedirectRoutes[i];

    if (item === pathname && isAuthenticated && !isMiscrositePage) {
      return PATH.QR.MAIN;
    }

    if (!isAuthenticated && !backRedirectRoutes.includes(pathname) && !isMiscrositePage) {
      // TODO:  check if we need to handle this edge case in some other place this is totally wrong
      // window.location.replace('/');
      return PATH.UNAUTH.MAIN;
    }
  }
};

// Type for `formatBytes`
export const formatBytes = (bytes: number, decimals = 2, isValue?: boolean): string | number => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  if (isValue) {
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
  }

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${sizes[i]}`;
};

// `getScalValue` function with typed parameter
export const getScalValue = (val: number): string => {
  return val === 100 ? '1' : `0.${val}`;
};

// `getRotateValue` function with typed parameter
export const getRotateValue = (imagesRotateValue: number): number => {
  return imagesRotateValue >= MAX_ROTATE_VALUE ? 0 : imagesRotateValue + 90;
};

// `logOut` function
export const logOut = async (): Promise<void> => {
  googleAnalytics.events.updateUserId(null);

  const cookies = await cookieService.getAllCookies();

  delete axios.defaults.headers.common['Authorization'];

  cookies.forEach((item) => {
    cookieService.deleteCookie(item.name);
  });
  localStorage.clear();

  window.location.replace(window.location.origin);
};

// `refreshToken` function
export const refreshToken = async (): Promise<string | undefined> => {
  const refreshToken = cookieService.getCookie('refresh');

  try {
    const freshData = await authenticationAPIService.refreshToken({ refresh: refreshToken });

    const { refresh, access, ...other } = freshData?.data || {};
    cookieService.setGroupOfCookies(other);
    if (access) {
      cookieService.setCookie('access', access, 365);
    }
    if (refresh) {
      cookieService.setCookie('refresh', refresh, 365);
    }

    return freshData.data.access;
  } catch (e) {
    logOut();
  }
};

// `camelizeProcessor` function
export const camelizeProcessor = (key: string, convert: Function, options: any): string => {
  const snakecase = new RegExp(/^[a-z0-9]+(?:_[a-z0-9]+)*$/);
  return snakecase.test(key) ? convert(key, options) : key;
};

// `decamelizeProcessor` function
export const decamelizeProcessor = (key: string, convert: Function, options: any): string => {
  const camelcase = new RegExp(/^[a-z]+(?:[A-Z0-9]+[a-z0-9]+[A-Za-z0-9]*)*$/);
  return camelcase.test(key) ? convert(key, options) : key;
};

// `parseErrorResponseMessage` function with typed error
export const parseErrorResponseMessage = (error: any, defaultMessage = ''): string => {
  const contentType = error.response?.headers?.['content-type'];
  if (contentType?.includes('application/json')) {
    const data = error?.response?.data || {};
    const key = Object.keys(data)?.[0];
    const message = data[key];

    switch (typeof message) {
      case 'string':
        return message;
      case 'object':
        return `${message[0]} ${key}` || defaultMessage;
      default:
        return defaultMessage;
    }
  }

  return defaultMessage;
};

// `getFileFromLocalSource` function with typed parameter
export const getFileFromLocalSource = async (url: string): Promise<File> => {
  const response = await fetch(url);
  const contentType = response.headers.get('content-type');
  const blob = await response.blob();
  return new File([blob], 'mockup', { contentType });
};

// `extractFirstNumbers` function
export const extractFirstNumbers = (str: string): string | null => {
  const number = parseInt(str, 10);
  return isNaN(number) ? null : number.toString();
};

// `convertDate` function with typed parameter
export const convertDate = (date: Date): string => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return date.toLocaleDateString('en-CA', options);
};

// `downloadImageFromUrl` function
// export const downloadImageFromUrl = async (imageSrc: string): Promise<void> => {
//   try {
//     const response = await fetch(imageSrc);
//     if (!response.ok) throw new Error('Network response was not ok');
//
//     const blob = await response.blob();
//     const url = window.URL.createObjectURL(blob);
//     const link = document.createElement('a');
//
//     link.href = url;
//     link.download = imageSrc.split('/').pop()!;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//     window.URL.revokeObjectURL(url);
//   } catch (error) {
//   }
// };

// `convertMicrositeTypeToId` function with typed parameter
export const convertMicrositeTypeToId = (type: string): string | undefined => {
  switch (type) {
    case 'website':
      return '0';
    case 'pdf':
      return '1';
    case 'links':
      return '2';
    case 'app':
      return '3';
    case 'images':
      return '4';
    case 'vcard-plus':
      return '5';
    case 'social-media':
      return '6';
    case 'coupon':
      return '7';
    default:
      return;
  }
};

// `getLocationTypeByTab` function with typed parameter
export const getLocationTypeByTab = (tab: string): string => {
  switch (tab) {
    case '1':
      return 'FULL';
    case '2':
      return 'URL';
    case '3':
      return 'COORDINATES';
    default:
      return 'FULL';
  }
};

// `isMaxLength` function with typed parameters
export const isMaxLength = (value: string | undefined, maxLength: number): string => {
  return value?.length >= maxLength ? t('other:maxLength', { maxLength }) : '';
};

// `isRequiredFieldPresent` function with typed parameters
export const isRequiredFieldPresent = (dispatch: Function, isMicrositeDataValid: boolean): void => {
  const errorClass = document.querySelector('.MuiFormHelperText-root.Mui-error') || '';
  const requiredErrorColor = errorClass ? window.getComputedStyle(errorClass)?.color : errorClass;
  const redMuiColor = 'rgb(211, 47, 47)';
  const whiteColor = 'rgb(244, 241, 241)';
  const stepperActions = stepperSlice.actions;

  if (errorClass && (requiredErrorColor === redMuiColor || requiredErrorColor === whiteColor) && isMicrositeDataValid) {
    dispatch(stepperActions.setIsMicrositeDataValid(false));
  } else {
    if (!isMicrositeDataValid && requiredErrorColor !== redMuiColor && requiredErrorColor !== whiteColor) {
      dispatch(stepperActions.setIsMicrositeDataValid(true));
    }
  }
};

// `isSecureProtocol` function
export const isSecureProtocol = window.location.protocol.toLocaleLowerCase() === 'https:' ? 'secure;' : '';

// `cutString` function with typed parameters
export const cutString = (str: string, start: number, end: number): string => {
  return str?.length > 40 ? `${str.slice(start, end)}...` : str;
};

// `fileSizeInKb` function with typed parameter
export const fileSizeInKb = (file: number): number => {
  return file / 1024;
};

// `getCurrentUTCOffset` function
export const getCurrentUTCOffset = (): string => {
  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes <= 0 ? '+' : '-';

  return `UTC${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutesRemainder).padStart(2, '0')}`;
};

// `formatAddress` function with typed parameter
export const formatAddress = (location: {
  street?: string;
  number?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
}): string => {
  const { street, number, postalCode, city, state, country } = location;

  const addressParts: string[] = [];

  if (street && number) {
    addressParts.push(`${street} ${number}`);
  } else if (street) {
    addressParts.push(street);
  }

  if (postalCode) {
    addressParts.push(postalCode);
  }

  if (city) {
    addressParts.push(city);
  }

  if (state) {
    addressParts.push(state);
  }

  if (country) {
    addressParts.push(country);
  }
  return addressParts.join(', ');
};

// `detectUserApiHost` function
export const detectUserApiHost = (): string => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return timeZone.includes('Europe') ? 'https://eu.i.posthog.com' : 'https://us.i.posthog.com';
};

// `getBillingTranslation` function with typed parameters
export const getBillingTranslation = (interval: string, intervalCount: number): string => {
  switch (interval) {
    case 'months':
      switch (intervalCount) {
        case 3:
          return t('plansAndPrices:billedQuarterly');
        case 6:
          return t('plansAndPrices:billedSemiAnnually');
        default:
          return '';
      }
    case 'year':
      return t('plansAndPrices:billedAnnually');
    default:
      return '';
  }
};

export const getMaxLengthDisplayedValue = ({ value, maxLength }: { value?: string; maxLength: number }) => {
  if (!value || value.length <= maxLength) return value;

  return `${value.slice(0, maxLength)}...`;
};
